/**
 * OwnerOnboardingAdmin — superadmin panel for reviewing owner onboarding
 * submissions, verifying them, and sending magic login links.
 */
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { getSiteUrlForPath } from "@/lib/site-url";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle2,
  XCircle,
  Clock,
  Mail,
  Eye,
  ChevronRight,
  User,
  Building2,
  CreditCard,
  Landmark,
  FileText,
  Download,
  Loader2,
  X,
  ExternalLink,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { de, enGB } from "date-fns/locale";
import { formatAddress } from "@/lib/v2-format";
import { useLanguage } from "@/contexts/LanguageContext";
import { StoredFileLink } from "@/components/shared/StoredFileLink";

// ── Types ──────────────────────────────────────────────────────────────────

interface Submission {
  id: string;
  name: string;
  email: string;
  address: string | null;
  phone: string | null;
  bank_holder: string | null;
  iban: string | null;
  bic: string | null;
  payment_method: string;
  agb_accepted: boolean;
  verwaltervollmacht_accepted: boolean;
  withdrawal_policy_accepted: boolean;
  management_agreement_accepted: boolean;
  reference_number: string | null;
  submitted_at: string;
  status: "pending" | "verified" | "rejected";
  verified_by: string | null;
  verified_at: string | null;
  rejection_reason: string | null;
  supabase_user_id: string | null;
  token: {
    email: string;
    property_id: number | null;
    prefilled_fee_amount: number | null;
    prefilled_fee_model: string | null;
  } | null;
  property?: {
    title: string | null;
    address: string | null;
    city: string | null;
  } | null;
}

// ── Status badge ───────────────────────────────────────────────────────────

function StatusBadge({
  status,
  t,
}: {
  status: string;
  t: (key: string, fallback: string) => string;
}) {
  if (status === "pending") {
    return (
      <Badge className="bg-yellow-50 text-yellow-700 border border-yellow-200 flex items-center gap-1 w-fit">
        <Clock className="w-3 h-3" />{" "}
        {t("admin.ownerOnboarding.status.pending", "Pending")}
      </Badge>
    );
  }
  if (status === "verified") {
    return (
      <Badge className="bg-green-50 text-green-700 border border-green-200 flex items-center gap-1 w-fit">
        <CheckCircle2 className="w-3 h-3" />{" "}
        {t("admin.ownerOnboarding.status.verified", "Verified")}
      </Badge>
    );
  }
  return (
    <Badge className="bg-red-50 text-red-700 border border-red-200 flex items-center gap-1 w-fit">
      <XCircle className="w-3 h-3" />{" "}
      {t("admin.ownerOnboarding.status.rejected", "Rejected")}
    </Badge>
  );
}

/** Email the owner a login link. Resolves to the failure reason, or null once sent. */
async function sendOwnerLoginLink(
  email: string,
  fallback: string
): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke<{ error?: string }>(
    "admin-send-login-link",
    { body: { email, redirect_to: getSiteUrlForPath("/dashboard") } }
  );
  if (error) return edgeFunctionErrorMessage(error, fallback);
  return data?.error ?? null;
}

// ── Review panel ───────────────────────────────────────────────────────────

function ReviewPanel({
  sub,
  onClose,
  onVerified,
}: {
  sub: Submission;
  onClose: () => void;
  onVerified: () => void;
}) {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const dateLocale = language === "de" ? de : enGB;
  const [verifying, setVerifying] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [signedAt, setSignedAt] = useState<string | null>(null);
  const [signedDocs, setSignedDocs] = useState<{ name: string; url: string }[]>(
    []
  );

  useEffect(() => {
    // Fetch signature image (first signed contract)

    supabase
      .from("owner_signed_contracts")
      .select(
        "owner_signature_data, signed_at, signed_pdf_url, template_id, owner_contract_templates(name)"
      )
      .eq("submission_id", sub.id)
      .not("signed_pdf_url", "is", null)
      .order("signed_at", { ascending: true })
      .then(
        ({
          data,
        }: {
          data:
            | {
                owner_signature_data: string | null;
                signed_at: string | null;
                signed_pdf_url: string | null;
                template_id: string | null;
                owner_contract_templates: { name: string } | null;
              }[]
            | null;
        }) => {
          if (data && data.length > 0) {
            setSignatureData(data[0].owner_signature_data);
            setSignedAt(data[0].signed_at);
            setSignedDocs(
              data
                .filter((r) => r.signed_pdf_url)
                .map((r) => ({
                  name:
                    r.owner_contract_templates?.name ?? "Verwaltervollmacht",
                  url: r.signed_pdf_url!,
                }))
            );
          }
        }
      );

    // Fetch signed contracts + AGB (platform_documents, fallback to owner_contract_templates)
    Promise.all([
      supabase
        .from("owner_signed_contracts")
        .select("signed_pdf_url, owner_contract_templates(name)")
        .eq("submission_id", sub.id),
      supabase
        .from("platform_documents")
        .select("file_url, title")
        .eq("document_key", "agb")
        .maybeSingle(),
      supabase
        .from("owner_contract_templates")
        .select("name, pdf_url")
        .eq("kind", "attach")
        .not("pdf_url", "is", null)
        .order("created_at", { ascending: true }),
    ]).then(
      ([{ data: contractRows }, { data: agbDoc }, { data: attachTpls }]) => {
        const docs: { name: string; url: string }[] = [];
        for (const row of contractRows ?? []) {
          if (!row.signed_pdf_url) continue;
          const tpl = row.owner_contract_templates as { name?: string } | null;
          docs.push({
            name: tpl?.name || "Verwaltervollmacht",
            url: row.signed_pdf_url,
          });
        }
        // Append AGB — prefer platform_documents, fall back to owner_contract_templates (kind=attach)
        const alreadyHasAgb = docs.some((d) =>
          d.name.toLowerCase().includes("agb")
        );
        if (!alreadyHasAgb) {
          const agbUrl = agbDoc?.file_url ?? attachTpls?.[0]?.pdf_url ?? null;
          const agbName =
            (agbDoc as { title?: string | null } | null)?.title ??
            attachTpls?.[0]?.name ??
            "AGB Verwaltungsvertrag";
          if (agbUrl) docs.push({ name: agbName, url: agbUrl });
        }
        // Append Auftragsbestätigung — stored at a known path in the contracts
        // bucket. A bare path: the links below sign it on click (COL-436).
        if (sub.reference_number) {
          docs.push({
            name: "Auftragsbestätigung",
            url: `auftragsbestaetigung/${sub.id}_${sub.reference_number}.pdf`,
          });
        }
        setSignedDocs(docs);
      }
    );
  }, [sub.id]);

  async function handleVerify() {
    setVerifying(true);
    try {
      // 1. Create Supabase auth user
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await supabase.functions.invoke("admin-invite-user", {
        body: {
          email: sub.email,
          name: sub.name,
          role: "owner",
          property_id: sub.token?.property_id,
          bank_account_holder: sub.bank_holder,
          bank_account_iban: sub.iban,
          bank_account_bic: sub.bic,
          skip_invite_email: true,
        },
      });

      if (res.error) throw new Error(res.error.message);

      const newUserId = res.data?.user_id;

      // 2. Create owner_document_visibility rows
      if (newUserId) {
        await supabase.from("owner_document_visibility").insert([
          {
            user_id: newUserId,
            document_key: "verwaltervollmacht",
            is_enabled: true,
          },
          { user_id: newUserId, document_key: "agb", is_enabled: true },
        ]);
      }

      // 3. Update submission status
      await supabase
        .from("owner_onboarding_submissions")
        .update({
          status: "verified",
          verified_at: new Date().toISOString(),
          supabase_user_id: newUserId ?? null,
        })
        .eq("id", sub.id);

      // 4. Send magic login link. The owner is verified either way, so a failed
      // link is reported as that, not as a failed verification; the admin can
      // resend it from the verified view.
      const linkFailure = await sendOwnerLoginLink(
        sub.email,
        t("admin.ownerOnboarding.toast.unknownError", "Unknown error")
      );
      if (linkFailure) {
        toast({
          title: t(
            "admin.ownerOnboarding.toast.verifiedLinkFailedTitle",
            "Owner verified, but the login link was not sent"
          ),
          description: linkFailure,
          variant: "destructive",
        });
      } else {
        toast({
          title: t(
            "admin.ownerOnboarding.toast.verifiedTitle",
            "Owner verified"
          ),
          description: `${t("admin.ownerOnboarding.toast.loginLinkSentTo", "Login link sent to")} ${sub.email}`,
        });
      }
      onVerified();
    } catch (err: unknown) {
      toast({
        title: t(
          "admin.ownerOnboarding.toast.verificationFailed",
          "Verification failed"
        ),
        description:
          err instanceof Error
            ? err.message
            : t("admin.ownerOnboarding.toast.unknownError", "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setVerifying(false);
    }
  }

  async function handleReject() {
    setRejecting(true);
    await supabase
      .from("owner_onboarding_submissions")
      .update({ status: "rejected", rejection_reason: rejectReason || null })
      .eq("id", sub.id);
    toast({
      title: t(
        "admin.ownerOnboarding.toast.submissionRejected",
        "Submission rejected"
      ),
    });
    setRejecting(false);
    onVerified();
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <div className="w-full max-w-lg bg-white shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="font-bold text-gray-900">
              {t("admin.ownerOnboarding.reviewSubmission", "Review Submission")}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {sub.reference_number}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <StatusBadge status={sub.status} t={t} />

          {/* Owner details */}
          <Section
            icon={<User className="w-4 h-4" />}
            title={t("admin.ownerOnboarding.section.owner", "Owner")}
          >
            <Row
              label={t("admin.ownerOnboarding.field.name", "Name")}
              value={sub.name}
            />
            <Row
              label={t("admin.ownerOnboarding.field.email", "Email")}
              value={sub.email}
            />
            <Row
              label={t("admin.ownerOnboarding.field.phone", "Phone")}
              value={sub.phone}
            />
            <Row
              label={t("admin.ownerOnboarding.field.address", "Address")}
              value={sub.address}
            />
          </Section>

          {/* Property */}
          {sub.property && (
            <Section
              icon={<Building2 className="w-4 h-4" />}
              title={t("admin.ownerOnboarding.section.property", "Property")}
            >
              <Row
                label={t("admin.ownerOnboarding.field.title", "Title")}
                value={sub.property.title}
              />
              <Row
                label={t("admin.ownerOnboarding.field.address", "Address")}
                value={formatAddress(
                  sub.property.address,
                  (sub.property as { zip_code?: string | null }).zip_code,
                  sub.property.city
                )}
              />
              {sub.token?.prefilled_fee_amount && (
                <Row
                  label={t(
                    "admin.ownerOnboarding.field.managementFee",
                    "Management fee"
                  )}
                  value={`${sub.token.prefilled_fee_amount} €${t(
                    "admin.ownerOnboarding.field.perMonthSuffix",
                    "/month"
                  )}`}
                />
              )}
            </Section>
          )}

          {/* Bank / payment */}
          <Section
            icon={<Landmark className="w-4 h-4" />}
            title={t(
              "admin.ownerOnboarding.section.bankDetails",
              "Bank details"
            )}
          >
            <Row
              label={t(
                "admin.ownerOnboarding.field.accountHolder",
                "Account holder"
              )}
              value={sub.bank_holder}
            />
            <Row label="IBAN" value={sub.iban} mono />
            <Row label="BIC" value={sub.bic} mono />
            <Row
              label={t(
                "admin.ownerOnboarding.field.paymentMethod",
                "Payment method"
              )}
              value={
                sub.payment_method === "sepa"
                  ? t(
                      "admin.ownerOnboarding.paymentMethod.sepa",
                      "SEPA Direct Debit"
                    )
                  : t(
                      "admin.ownerOnboarding.paymentMethod.creditCard",
                      "Credit Card"
                    )
              }
            />
          </Section>

          {/* Signed / submitted documents */}
          {signedDocs.length > 0 && (
            <Section
              icon={<FileText className="w-4 h-4" />}
              title={t("admin.ownerOnboarding.section.documents", "Documents")}
            >
              <div className="space-y-2 pt-1">
                {signedDocs.map((doc) => (
                  <div
                    key={doc.url}
                    className="flex items-center justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2.5 border border-gray-100"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                      <span className="text-sm text-gray-800 truncate font-medium">
                        {doc.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {/* Signed on click: a stored public URL or bare path
                          in the contracts bucket (COL-436). */}
                      <StoredFileLink
                        value={doc.url}
                        bucket="contracts"
                        errorText={t(
                          "admin.docs.openFailed",
                          "Could not open the document."
                        )}
                        className="flex items-center gap-1 text-xs font-semibold text-teal-600 bg-teal-50 hover:bg-teal-100 px-2.5 py-1.5 rounded-md transition-colors"
                        title={t("common.view", "View")}
                      >
                        <ExternalLink className="w-3 h-3" />{" "}
                        {t("common.view", "View")}
                      </StoredFileLink>
                      <StoredFileLink
                        value={doc.url}
                        bucket="contracts"
                        download
                        errorText={t(
                          "admin.docs.openFailed",
                          "Could not open the document."
                        )}
                        className="flex items-center gap-1 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 px-2.5 py-1.5 rounded-md transition-colors"
                        title={t("common.download", "Download")}
                      >
                        <Download className="w-3 h-3" />{" "}
                        {t("common.download", "Download")}
                      </StoredFileLink>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}

          {/* Accepted docs */}
          <Section
            icon={<FileText className="w-4 h-4" />}
            title={t(
              "admin.ownerOnboarding.section.acceptedDocuments",
              "Accepted documents"
            )}
          >
            <CheckRow
              label={t(
                "admin.ownerOnboarding.doc.managementAgreement",
                "Management Agreement"
              )}
              checked={sub.management_agreement_accepted}
            />
            <CheckRow
              label="AGB Verwaltungsvertrag"
              checked={sub.agb_accepted}
            />
            <CheckRow
              label="Verwaltervollmacht"
              checked={sub.verwaltervollmacht_accepted}
            />
            <CheckRow
              label={t(
                "admin.ownerOnboarding.doc.withdrawalPolicy",
                "Withdrawal Policy"
              )}
              checked={sub.withdrawal_policy_accepted}
            />
          </Section>

          {/* Documents */}
          <Section
            icon={<FileText className="w-4 h-4" />}
            title={t("admin.ownerOnboarding.section.documents", "Documents")}
          >
            {signedDocs.length === 0 ? (
              <p className="text-sm text-gray-400">
                {t(
                  "admin.ownerOnboarding.noDocumentsRecorded",
                  "No documents recorded"
                )}
              </p>
            ) : (
              <div className="space-y-2">
                {signedDocs.map(({ name: docName, url }) => (
                  <div
                    key={url}
                    className="flex items-center justify-between gap-2 bg-gray-50 rounded-lg border border-gray-100 px-3 py-2"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-3.5 h-3.5 text-teal-500 shrink-0" />
                      <span className="text-sm font-medium text-gray-800 truncate">
                        {docName}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <StoredFileLink
                        value={url}
                        bucket="contracts"
                        errorText={t(
                          "admin.docs.openFailed",
                          "Could not open the document."
                        )}
                        className="flex items-center gap-1 text-xs font-medium text-teal-600 hover:text-teal-800 px-2 py-1 rounded-md hover:bg-teal-50 transition-colors"
                      >
                        <Eye className="w-3 h-3" /> {t("common.view", "View")}
                      </StoredFileLink>
                      <StoredFileLink
                        value={url}
                        bucket="contracts"
                        download
                        errorText={t(
                          "admin.docs.openFailed",
                          "Could not open the document."
                        )}
                        className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100 transition-colors"
                      >
                        <Download className="w-3 h-3" />{" "}
                        {t("common.download", "Download")}
                      </StoredFileLink>
                    </div>
                  </div>
                ))}
                {signedAt && (
                  <p className="text-xs text-gray-400 pt-1">
                    {t("admin.ownerOnboarding.signed", "Signed")}{" "}
                    {formatDistanceToNow(new Date(signedAt), {
                      addSuffix: true,
                      locale: dateLocale,
                    })}
                  </p>
                )}
              </div>
            )}
            {signatureData && (
              <div className="mt-3 bg-white rounded-lg border border-gray-200 p-2">
                <p className="text-xs text-gray-400 mb-1">
                  {t("admin.ownerOnboarding.ownerSignature", "Owner signature")}
                </p>
                <img
                  src={signatureData}
                  alt="Owner signature"
                  className="w-full max-h-20 object-contain"
                />
              </div>
            )}
          </Section>

          <div className="text-xs text-gray-400">
            {t("admin.ownerOnboarding.submitted", "Submitted")}{" "}
            {formatDistanceToNow(new Date(sub.submitted_at), {
              addSuffix: true,
              locale: dateLocale,
            })}
          </div>

          {sub.status === "rejected" && sub.rejection_reason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <strong>
                {t("admin.ownerOnboarding.rejectionReason", "Rejection reason")}
                :
              </strong>{" "}
              {sub.rejection_reason}
            </div>
          )}
        </div>

        {/* Actions */}
        {sub.status === "pending" && (
          <div className="p-4 border-t border-gray-100 space-y-3">
            {showRejectForm ? (
              <div className="space-y-2">
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t(
                    "admin.ownerOnboarding.rejectReasonPlaceholder",
                    "Rejection reason (optional)..."
                  )}
                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-400"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowRejectForm(false)}
                    className="flex-1"
                  >
                    {t("common.cancel", "Cancel")}
                  </Button>
                  <Button
                    onClick={handleReject}
                    disabled={rejecting}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  >
                    {rejecting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      t("admin.ownerOnboarding.confirmReject", "Confirm reject")
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowRejectForm(true)}
                  className="flex-1 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />{" "}
                  {t("admin.ownerOnboarding.reject", "Reject")}
                </Button>
                <Button
                  onClick={handleVerify}
                  disabled={verifying}
                  className="flex-1 bg-teal-500 hover:bg-teal-600 text-white"
                >
                  {verifying ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />{" "}
                      {t("admin.ownerOnboarding.verifying", "Verifying...")}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />{" "}
                      {t(
                        "admin.ownerOnboarding.verifyAndSend",
                        "Verify & Send Login Link"
                      )}
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {sub.status === "verified" && (
          <div className="p-4 border-t border-gray-100">
            <Button
              onClick={async () => {
                const failure = await sendOwnerLoginLink(
                  sub.email,
                  t("admin.ownerOnboarding.toast.unknownError", "Unknown error")
                );
                toast(
                  failure
                    ? {
                        title: t(
                          "admin.ownerOnboarding.toast.loginLinkFailedTitle",
                          "Login link not sent"
                        ),
                        description: failure,
                        variant: "destructive",
                      }
                    : {
                        title: t(
                          "admin.ownerOnboarding.loginLinkResent",
                          "Login link resent"
                        ),
                        description: sub.email,
                      }
                );
              }}
              variant="outline"
              className="w-full border-teal-200 text-teal-600 hover:bg-teal-50"
            >
              <Mail className="w-4 h-4 mr-2" />{" "}
              {t("admin.ownerOnboarding.resendLoginLink", "Resend login link")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({
  icon,
  title,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
      <div className="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
        <span className="text-gray-400">{icon}</span> {title}
      </div>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-xs text-gray-400 min-w-[120px] shrink-0">
        {label}
      </span>
      <span
        className={`text-sm text-gray-900 font-medium ${mono ? "font-mono text-xs" : ""}`}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function CheckRow({ label, checked }: { label: string; checked: boolean }) {
  return (
    <div className="flex items-center gap-2">
      {checked ? (
        <CheckCircle2 className="w-4 h-4 text-teal-500 shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-300 shrink-0" />
      )}
      <span className="text-sm text-gray-700">{label}</span>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export default function OwnerOnboardingAdmin() {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const dateLocale = language === "de" ? de : enGB;
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Submission | null>(null);
  const [filter, setFilter] = useState<
    "all" | "pending" | "verified" | "rejected"
  >("all");
  // ?submission=<id> (colivIQ's onboarding card) opens that review once the
  // list has loaded. Consumed, so closing the panel doesn't reopen it.
  const [params, setParams] = useSearchParams();
  const linkedId = params.get("submission");

  useEffect(() => {
    loadSubmissions();
  }, []);

  useEffect(() => {
    if (!linkedId || loading) return;
    const match = submissions.find((s) => s.id === linkedId);
    if (match) setSelected(match);
    const next = new URLSearchParams(params);
    next.delete("submission");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedId, loading]);

  async function loadSubmissions() {
    setLoading(true);
    const { data, error } = await supabase
      .from("owner_onboarding_submissions")
      .select(
        `
        *,
        token:token_id (
          email, property_id, prefilled_fee_amount, prefilled_fee_model
        )
      `
      )
      .order("submitted_at", { ascending: false });

    if (error) {
      toast({
        title: t(
          "admin.ownerOnboarding.loadFailed",
          "Failed to load submissions"
        ),
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Load property details for each submission
    // status arrives as plain text; Submission narrows it to the values the
    // review flow writes.
    const rows = (data ?? []) as Submission[];
    const propertyIds = [
      ...new Set(rows.map((s) => s.token?.property_id).filter(Boolean)),
    ];
    const propertyMap: Record<
      string,
      { title: string | null; address: string | null; city: string | null }
    > = {};
    if (propertyIds.length > 0) {
      const { data: props } = await supabase
        .from("properties")
        .select("id, title, address, city, zip_code")
        .in("id", propertyIds);
      (props ?? []).forEach(
        (p: {
          id: number;
          title: string | null;
          address: string | null;
          city: string | null;
        }) => {
          propertyMap[p.id] = {
            title: p.title,
            address: p.address,
            city: p.city,
          };
        }
      );
    }

    setSubmissions(
      rows.map((s) => ({
        ...s,
        property: s.token?.property_id
          ? (propertyMap[s.token.property_id] ?? null)
          : null,
      }))
    );
    setLoading(false);
  }

  const filtered =
    filter === "all"
      ? submissions
      : submissions.filter((s) => s.status === filter);
  const pendingCount = submissions.filter((s) => s.status === "pending").length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            {t("admin.ownerOnboarding.title", "Owner Onboarding")}
            {pendingCount > 0 && (
              <span className="bg-yellow-100 text-yellow-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {pendingCount} {t("admin.ownerOnboarding.pending", "pending")}
              </span>
            )}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5">
            {t(
              "admin.ownerOnboarding.subtitle",
              "Review owner submissions and send login links after verification."
            )}
          </p>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit mb-5">
        {(["all", "pending", "verified", "rejected"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium capitalize transition-all ${
              filter === f
                ? "bg-white text-gray-900 shadow-sm"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {t(`admin.ownerOnboarding.filter.${f}`, f)}{" "}
            {f === "all" && `(${submissions.length})`}
            {f === "pending" && pendingCount > 0 && (
              <span className="ml-1 bg-yellow-200 text-yellow-800 text-xs px-1.5 py-0.5 rounded-full">
                {pendingCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-teal-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-xl">
          <User className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">
            {filter === "all"
              ? t("admin.ownerOnboarding.noSubmissions", "No submissions")
              : `${t("admin.ownerOnboarding.noFilteredSubmissionsPrefix", "No")} ${t(`admin.ownerOnboarding.filter.${filter}`, filter)} ${t("admin.ownerOnboarding.noFilteredSubmissionsSuffix", "submissions")}`}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((sub) => (
            <div
              key={sub.id}
              onClick={() => setSelected(sub)}
              className="flex items-center gap-4 bg-white rounded-xl border border-gray-200 shadow-sm p-4 hover:border-teal-200 hover:shadow-md cursor-pointer transition-all"
            >
              <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center shrink-0 text-sm font-bold text-teal-600">
                {sub.name?.charAt(0)?.toUpperCase() ?? "?"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-900 text-sm">
                    {sub.name}
                  </span>
                  <StatusBadge status={sub.status} t={t} />
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  <span className="text-xs text-gray-500">{sub.email}</span>
                  {sub.property?.title && (
                    <span className="text-xs text-gray-400">
                      · {sub.property.title}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-gray-400">
                  {formatDistanceToNow(new Date(sub.submitted_at), {
                    addSuffix: true,
                    locale: dateLocale,
                  })}
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400" />
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <ReviewPanel
          sub={selected}
          onClose={() => setSelected(null)}
          onVerified={() => {
            setSelected(null);
            loadSubmissions();
          }}
        />
      )}
    </div>
  );
}
